/**
 * E-Mail-Protokoll — welche System-Mail ging wann an wen.
 *
 * Datenquelle ist `GET /api/email-protokoll`, gespeist aus der Tabelle
 * `email_protokoll`, die beim Versand je Empfänger eine Zeile bekommt.
 *
 * Der Inhalt der Mails wird bewusst nicht gespeichert und ist hier deshalb
 * auch nicht abrufbar: Betreff, Empfänger, Zeitpunkt und Ergebnis genügen für
 * die Frage "hat das Mitglied seine Bestätigung bekommen?", ein vollständiges
 * Archiv aller Passwort- und Rechnungsmails wäre dagegen eine Datensammlung,
 * die niemand braucht.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AdjustmentsHorizontalIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  UserGroupIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../utils/apiClient';
import { StatCard, StatGroup } from '../components/StatCard';

interface Eintrag {
  id: number;
  gesendet_am: string;
  kategorie: string;
  anlass: string | null;
  empfaenger: string;
  empfaenger_name: string | null;
  member_id: number | null;
  kopie: 'an' | 'bcc';
  betreff: string;
  status: 'gesendet' | 'fehlgeschlagen';
  versuche: number;
  fehler: string | null;
}

interface Protokoll {
  gesamt: {
    mails: number;
    gesendet: number;
    fehlgeschlagen: number;
    empfaenger: number;
    mitglieder: number;
    erste: string | null;
    letzte: string | null;
  };
  nach_kategorie: { kategorie: string; bezeichnung: string; mails: number; fehlgeschlagen: number }[];
  verlauf: { tag: string; mails: number; fehlgeschlagen: number }[];
  eintraege: Eintrag[];
  seite: number;
  pro_seite: number;
  seiten: number;
}

interface Filterwerte {
  kategorien: { kategorie: string; bezeichnung: string; mails: number }[];
  anlaesse: { kategorie: string; anlass: string; mails: number }[];
  mitglieder: { member_id: number; name: string; email: string; mails: number; letzte: string }[];
}

interface Auswahl {
  suche: string;
  kategorie: string;
  anlass: string;
  status: string;
  member_id: string;
  von: string;
  bis: string;
}

const LEER: Auswahl = {
  suche: '',
  kategorie: '',
  anlass: '',
  status: '',
  member_id: '',
  von: '',
  bis: '',
};

/**
 * Lesbare Namen für den Anlass. Ist ein Anlass hier nicht hinterlegt (etwa weil
 * im Backend eine neue Mail-Art dazugekommen ist), wird der Schlüssel selbst
 * angezeigt — lieber ein technischer Name als eine leere Spalte.
 */
const ANLASS_TEXT: Record<string, string> = {
  buchungsbestaetigung: 'Buchungsbestätigung',
  stornierung: 'Stornierung',
  hinweis_an_ersteller: 'Hinweis an Ersteller',
  stornohinweis_an_ersteller: 'Stornohinweis an Ersteller',
  rundmail: 'Rundmail',
  neuer_termin: 'Neuer Termin',
  zahlung: 'Zahlung',
  korrektur: 'Korrektur',
  freischaltung: 'Freischaltung',
  aktivierungscode: 'Aktivierungscode',
  passwort_code: 'Passwort-Code',
  passwort_geaendert: 'Passwort geändert',
  passwort_zuruecksetzen: 'Passwort zurücksetzen',
  serie_geloescht: 'Serie gelöscht',
  aenderung: 'Terminänderung',
  anmeldung: 'Anmeldung',
  zahlung_erhalten: 'Zahlung erhalten',
  zahlungserinnerung: 'Zahlungserinnerung',
  anmeldung_vorstand: 'Anmeldung (an Vorstand)',
  anmeldung_bestaetigung: 'Anmeldebestätigung',
  abmeldung_bestaetigung: 'Abmeldebestätigung',
  abmeldung_vorstand: 'Abmeldung (an Vorstand)',
  smtp_test: 'SMTP-Test',
};

const anlassText = (anlass: string | null) => (anlass ? ANLASS_TEXT[anlass] || anlass : '—');

const zeitpunkt = (wert: string) =>
  new Date(wert).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

/** Datum im Format, das ein <input type="date"> erwartet. */
const alsFeld = (d: Date) => d.toISOString().slice(0, 10);

const vorTagen = (tage: number) => {
  const d = new Date();
  d.setDate(d.getDate() - tage);
  return alsFeld(d);
};

const ZEITRAEUME = [
  { label: '7 Tage', tage: 7 },
  { label: '30 Tage', tage: 30 },
  { label: '90 Tage', tage: 90 },
  { label: 'Alles', tage: 0 },
];

const StatusZeichen: React.FC<{ eintrag: Eintrag }> = ({ eintrag }) =>
  eintrag.status === 'gesendet' ? (
    <span className="inline-flex items-center gap-1 whitespace-nowrap text-[12px] font-semibold text-success-700 dark:text-success-400">
      <CheckCircleIcon className="h-4 w-4" aria-hidden="true" />
      gesendet
      {eintrag.versuche > 1 && (
        <span className="font-normal text-gray-400">({eintrag.versuche}. Versuch)</span>
      )}
    </span>
  ) : (
    <span
      className="inline-flex items-center gap-1 whitespace-nowrap text-[12px] font-semibold text-error-700 dark:text-error-400"
      title={eintrag.fehler || undefined}
    >
      <ExclamationTriangleIcon className="h-4 w-4" aria-hidden="true" />
      fehlgeschlagen
    </span>
  );

const AdminEmailProtokoll: React.FC = () => {
  const [daten, setDaten] = useState<Protokoll | null>(null);
  const [filterwerte, setFilterwerte] = useState<Filterwerte | null>(null);
  const [auswahl, setAuswahl] = useState<Auswahl>(LEER);
  // Das Suchfeld wird verzögert übernommen, damit nicht jeder Tastendruck eine
  // Abfrage auslöst.
  const [sucheEingabe, setSucheEingabe] = useState('');
  const [seite, setSeite] = useState(1);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [filterOffen, setFilterOffen] = useState(false);

  useEffect(() => {
    const zeit = setTimeout(() => {
      setAuswahl((a) => (a.suche === sucheEingabe ? a : { ...a, suche: sucheEingabe }));
      setSeite(1);
    }, 350);
    return () => clearTimeout(zeit);
  }, [sucheEingabe]);

  const abfrage = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(auswahl).forEach(([schluessel, wert]) => {
      if (!wert) return;
      // "extern" ist kein Mitglied, sondern die Gegenauswahl dazu: alle
      // Adressen ohne Mitgliedskonto (Warteliste, Gäste, Vorstandspostfach).
      if (schluessel === 'member_id' && wert === 'extern') p.set('nur_extern', '1');
      else p.set(schluessel, wert);
    });
    p.set('seite', String(seite));
    return p.toString();
  }, [auswahl, seite]);

  const holen = useCallback(async () => {
    setLaden(true);
    setFehler(null);
    try {
      const res = await apiClient.get(`/api/email-protokoll?${abfrage}`);
      setDaten(res.data);
    } catch (e) {
      console.error('E-Mail-Protokoll: Laden fehlgeschlagen', e);
      setFehler('Das Protokoll konnte nicht geladen werden.');
    } finally {
      setLaden(false);
    }
  }, [abfrage]);

  useEffect(() => {
    holen();
  }, [holen]);

  useEffect(() => {
    apiClient
      .get('/api/email-protokoll/filter')
      .then((res) => setFilterwerte(res.data))
      .catch((e) => console.error('E-Mail-Protokoll: Filter konnten nicht geladen werden', e));
  }, []);

  const setzen = (teil: Partial<Auswahl>) => {
    setAuswahl((a) => ({ ...a, ...teil }));
    setSeite(1);
  };

  const zeitraumSetzen = (tage: number) => {
    setzen(tage === 0 ? { von: '', bis: '' } : { von: vorTagen(tage), bis: '' });
  };

  const zuruecksetzen = () => {
    setSucheEingabe('');
    setAuswahl(LEER);
    setSeite(1);
  };

  const aktiveFilter = Object.entries(auswahl).filter(([, w]) => w).length;
  const g = daten?.gesamt;

  /** Anlässe passend zur gewählten Kategorie — sonst wäre die Liste unbrauchbar lang. */
  const anlaesse = (filterwerte?.anlaesse || []).filter(
    (a) => !auswahl.kategorie || a.kategorie === auswahl.kategorie
  );

  const maxVerlauf = Math.max(...(daten?.verlauf.map((v) => v.mails) || [0]), 1);

  /** Die Filterfelder — auf dem Handy im Slide-up, auf großen Schirmen fest sichtbar. */
  const Felder = (
    <div className="space-y-3">
      <div>
        <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          Kategorie
        </label>
        <select
          value={auswahl.kategorie}
          onChange={(e) => setzen({ kategorie: e.target.value, anlass: '' })}
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-[13px] text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
        >
          <option value="">Alle Kategorien</option>
          {(filterwerte?.kategorien || []).map((k) => (
            <option key={k.kategorie} value={k.kategorie}>
              {k.bezeichnung} ({k.mails})
            </option>
          ))}
        </select>
      </div>

      {anlaesse.length > 0 && (
        <div>
          <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            Anlass
          </label>
          <select
            value={auswahl.anlass}
            onChange={(e) => setzen({ anlass: e.target.value })}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-[13px] text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
          >
            <option value="">Alle Anlässe</option>
            {anlaesse.map((a) => (
              <option key={`${a.kategorie}-${a.anlass}`} value={a.anlass}>
                {anlassText(a.anlass)} ({a.mails})
              </option>
            ))}
          </select>
        </div>
      )}

      <div>
        <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          Mitglied
        </label>
        <select
          value={auswahl.member_id}
          onChange={(e) => setzen({ member_id: e.target.value })}
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-[13px] text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
        >
          <option value="">Alle Empfänger</option>
          <option value="extern">Nur Nicht-Mitglieder</option>
          {(filterwerte?.mitglieder || []).map((m) => (
            <option key={m.member_id} value={m.member_id}>
              {m.name} ({m.mails})
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          Status
        </label>
        <select
          value={auswahl.status}
          onChange={(e) => setzen({ status: e.target.value })}
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-[13px] text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
        >
          <option value="">Alle</option>
          <option value="gesendet">Nur gesendet</option>
          <option value="fehlgeschlagen">Nur fehlgeschlagen</option>
        </select>
      </div>

      <div>
        <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          Zeitraum
        </label>
        <div className="mb-2 flex flex-wrap gap-1.5">
          {ZEITRAEUME.map((z) => (
            <button
              key={z.label}
              type="button"
              onClick={() => zeitraumSetzen(z.tage)}
              className="rounded-md border border-gray-300 px-2.5 py-1 text-[12px] font-semibold text-gray-600 transition-colors hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              {z.label}
            </button>
          ))}
        </div>
        {/*
          Die beiden Datumsfelder stehen untereinander: ein <input type="date">
          bringt in WebKit eine feste Mindestbreite mit, zwei davon nebeneinander
          laufen in der schmalen Filterspalte über den Rand hinaus.
        */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="w-7 shrink-0 text-[12px] text-gray-400">von</span>
            <input
              type="date"
              value={auswahl.von}
              onChange={(e) => setzen({ von: e.target.value })}
              className="min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-[13px] text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="w-7 shrink-0 text-[12px] text-gray-400">bis</span>
            <input
              type="date"
              value={auswahl.bis}
              onChange={(e) => setzen({ bis: e.target.value })}
              className="min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-[13px] text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            />
          </div>
        </div>
      </div>

      {aktiveFilter > 0 && (
        <button
          type="button"
          onClick={zuruecksetzen}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-[13px] font-semibold text-gray-600 transition-colors hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
        >
          Filter zurücksetzen
        </button>
      )}
    </div>
  );

  return (
    <div className="mx-auto max-w-7xl">
      <header className="app-page-header mb-6 hidden border-b-[3px] border-lime-400 bg-white dark:bg-gray-800 sm:block">
        <div className="flex items-center justify-between gap-3 p-4 md:px-8 md:py-6">
          <div className="flex items-center gap-3">
            <EnvelopeIcon className="h-7 w-7 text-primary-600 dark:text-primary-400" />
            <div>
              <h1 className="text-[28px] font-bold text-gray-900 dark:text-white">E-Mail-Protokoll</h1>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Welche System-Mail ging wann an wen — Empfänger, Betreff und Ergebnis, ohne Inhalt
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={holen}
            disabled={laden}
            aria-label="Neu laden"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-300 text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            <ArrowPathIcon className={`h-4 w-4 ${laden ? 'animate-spin' : ''}`} aria-hidden="true" />
          </button>
        </div>
      </header>

      {/*
        Werkzeugleiste für die Breiten ohne Filterspalte. Der Kopf oben ist auf
        dem Handy ausgeblendet (dort trägt der Layout-Header den Titel), deshalb
        sitzt der Neu-laden-Knopf hier zusätzlich — aber nur unter sm, sonst
        stünde er zweimal auf der Seite.
      */}
      <div className="mb-4 flex items-center gap-2 lg:hidden">
        <button
          type="button"
          onClick={() => setFilterOffen(true)}
          className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-[13px] font-semibold text-gray-600 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
        >
          <AdjustmentsHorizontalIcon className="h-4 w-4" aria-hidden="true" />
          Filter
          {aktiveFilter > 0 && (
            <span className="rounded-full bg-primary-600 px-1.5 text-[11px] font-bold text-white">
              {aktiveFilter}
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={holen}
          disabled={laden}
          aria-label="Neu laden"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-300 text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-50 sm:hidden dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
        >
          <ArrowPathIcon className={`h-4 w-4 ${laden ? 'animate-spin' : ''}`} aria-hidden="true" />
        </button>
      </div>

      {fehler && (
        <p className="mb-4 rounded-lg bg-error-50 px-3 py-2 text-sm font-semibold text-error-700 dark:bg-error-900/20 dark:text-error-300">
          {fehler}
        </p>
      )}

      {/* Kennzahlen */}
      <StatGroup className="mb-5" columns={4} mobileColumns={2}>
        <StatCard
          icon={EnvelopeIcon}
          tone="primary"
          label="Mails"
          value={(g?.mails ?? 0).toLocaleString('de-DE')}
          sub={aktiveFilter > 0 ? 'im Filter' : 'insgesamt'}
        />
        <StatCard
          icon={CheckCircleIcon}
          tone="success"
          valueTone="success"
          label="Gesendet"
          value={(g?.gesendet ?? 0).toLocaleString('de-DE')}
          sub={g?.letzte ? `zuletzt ${zeitpunkt(g.letzte)}` : undefined}
        />
        <StatCard
          icon={ExclamationTriangleIcon}
          tone={(g?.fehlgeschlagen ?? 0) > 0 ? 'error' : 'neutral'}
          valueTone={(g?.fehlgeschlagen ?? 0) > 0 ? 'error' : 'neutral'}
          label="Fehlgeschlagen"
          value={(g?.fehlgeschlagen ?? 0).toLocaleString('de-DE')}
          sub={(g?.fehlgeschlagen ?? 0) > 0 ? 'nach allen Versuchen' : 'keine Ausfälle'}
        />
        <StatCard
          icon={UserGroupIcon}
          tone="info"
          label="Empfänger"
          value={(g?.empfaenger ?? 0).toLocaleString('de-DE')}
          sub={`davon ${g?.mitglieder ?? 0} Mitglieder`}
        />
      </StatGroup>

      <div className="grid gap-5 lg:grid-cols-[280px_minmax(0,1fr)]">
        {/* Filterspalte — auf dem Handy steckt sie im Slide-up */}
        <div className="hidden min-w-0 lg:block">
          <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-[#1c2432]">
            <h2 className="mb-3 text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Filter
            </h2>
            {Felder}
          </div>

          {(daten?.nach_kategorie.length ?? 0) > 0 && (
            <div className="mt-4 overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-[#1c2432]">
              <h2 className="border-b border-gray-200 px-4 py-2.5 text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:border-gray-700 dark:text-gray-400">
                Nach Kategorie
              </h2>
              {daten!.nach_kategorie.map((k) => (
                <button
                  key={k.kategorie}
                  type="button"
                  onClick={() =>
                    setzen({ kategorie: auswahl.kategorie === k.kategorie ? '' : k.kategorie, anlass: '' })
                  }
                  className={`flex w-full items-center justify-between gap-3 border-b border-gray-100 px-4 py-2.5 text-left transition-colors last:border-b-0 hover:bg-gray-50 dark:border-gray-700/60 dark:hover:bg-gray-700/40 ${
                    auswahl.kategorie === k.kategorie ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                  }`}
                >
                  <span className="min-w-0 truncate text-[13px] font-semibold text-gray-900 dark:text-white">
                    {k.bezeichnung}
                  </span>
                  <span className="flex shrink-0 items-center gap-1.5 text-[12px] text-gray-400">
                    {k.fehlgeschlagen > 0 && (
                      <span className="rounded bg-error-50 px-1.5 py-0.5 text-[11px] font-bold text-error-600 dark:bg-error-900/30 dark:text-error-400">
                        {k.fehlgeschlagen} Fehler
                      </span>
                    )}
                    {k.mails}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="min-w-0 space-y-4">
          {/* Suche */}
          <div className="relative">
            <MagnifyingGlassIcon
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
              aria-hidden="true"
            />
            <input
              type="search"
              value={sucheEingabe}
              onChange={(e) => setSucheEingabe(e.target.value)}
              placeholder="Betreff, Name oder E-Mail-Adresse …"
              className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-[13px] text-gray-900 placeholder:text-gray-400 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            />
          </div>

          {/* Verlauf */}
          {(daten?.verlauf.length ?? 0) > 1 && (
            <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-[#1c2432]">
              <h2 className="mb-3 text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                Verlauf
              </h2>
              <div className="flex h-20 items-end gap-0.5">
                {daten!.verlauf.map((v) => (
                  <div
                    key={v.tag}
                    className="flex-1 rounded-t bg-primary-500/70 transition-colors hover:bg-primary-600"
                    style={{ height: `${Math.max((v.mails / maxVerlauf) * 100, 3)}%` }}
                    title={`${new Date(v.tag).toLocaleDateString('de-DE')}: ${v.mails} Mails${
                      v.fehlgeschlagen > 0 ? `, ${v.fehlgeschlagen} fehlgeschlagen` : ''
                    }`}
                  />
                ))}
              </div>
              <p className="mt-2 text-[11px] text-gray-400">
                {daten!.verlauf.length} Tage mit Versand, höchstens {maxVerlauf} Mails an einem Tag
              </p>
            </div>
          )}

          {/* Liste */}
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-[#1c2432]">
            {laden && !daten ? (
              <p className="py-16 text-center text-sm text-gray-500 dark:text-gray-400">
                Protokoll wird geladen …
              </p>
            ) : (daten?.eintraege.length ?? 0) === 0 ? (
              <div className="py-16 text-center">
                <EnvelopeIcon
                  className="mx-auto h-8 w-8 text-gray-300 dark:text-gray-600"
                  aria-hidden="true"
                />
                <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                  {aktiveFilter > 0
                    ? 'Zu diesen Filtern gibt es keine Einträge.'
                    : 'Es wurde noch keine Mail protokolliert.'}
                </p>
                {aktiveFilter === 0 && (
                  <p className="mt-1 text-[12px] text-gray-400 dark:text-gray-500">
                    Aufgezeichnet wird ab sofort — vorher versendete Mails fehlen hier.
                  </p>
                )}
              </div>
            ) : (
              <>
                {/* Desktop: Tabelle */}
                <table className="hidden w-full text-[12.5px] sm:table">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:border-gray-700 dark:text-gray-400">
                      <th className="px-4 py-2.5 font-bold">Zeitpunkt</th>
                      <th className="px-2 py-2.5 font-bold">Empfänger</th>
                      <th className="px-2 py-2.5 font-bold">Betreff</th>
                      <th className="px-2 py-2.5 font-bold">Anlass</th>
                      <th className="px-4 py-2.5 font-bold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {daten!.eintraege.map((e) => (
                      <tr
                        key={e.id}
                        className="border-b border-gray-100 last:border-b-0 dark:border-gray-700/60"
                      >
                        <td className="whitespace-nowrap px-4 py-2.5 text-gray-400 dark:text-gray-500">
                          {zeitpunkt(e.gesendet_am)}
                        </td>
                        <td className="max-w-[200px] px-2 py-2.5">
                          <p className="truncate font-semibold text-gray-900 dark:text-white">
                            {e.empfaenger_name || e.empfaenger}
                            {e.kopie === 'bcc' && (
                              <span className="ml-1 rounded bg-gray-100 px-1 text-[10px] font-bold uppercase text-gray-500 dark:bg-gray-700 dark:text-gray-300">
                                bcc
                              </span>
                            )}
                          </p>
                          <p className="truncate text-[11px] text-gray-400">{e.empfaenger}</p>
                        </td>
                        <td className="max-w-[280px] px-2 py-2.5">
                          <p className="truncate text-gray-900 dark:text-white" title={e.betreff}>
                            {e.betreff}
                          </p>
                        </td>
                        <td className="whitespace-nowrap px-2 py-2.5 text-gray-500 dark:text-gray-400">
                          {anlassText(e.anlass)}
                        </td>
                        <td className="px-4 py-2.5">
                          <StatusZeichen eintrag={e} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Handy: Karten, damit nichts seitlich verschwindet */}
                <div className="sm:hidden">
                  {daten!.eintraege.map((e) => (
                    <div
                      key={e.id}
                      className="border-b border-gray-100 px-4 py-3 last:border-b-0 dark:border-gray-700/60"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="min-w-0 flex-1 truncate text-[13px] font-semibold text-gray-900 dark:text-white">
                          {e.empfaenger_name || e.empfaenger}
                        </p>
                        <span className="shrink-0 text-[11px] text-gray-400">
                          {zeitpunkt(e.gesendet_am)}
                        </span>
                      </div>
                      <p className="mt-0.5 truncate text-[11px] text-gray-400">{e.empfaenger}</p>
                      <p className="mt-1 text-[12.5px] text-gray-700 dark:text-gray-200">{e.betreff}</p>
                      <div className="mt-1.5 flex items-center justify-between gap-2">
                        <span className="truncate text-[11px] text-gray-400">{anlassText(e.anlass)}</span>
                        <StatusZeichen eintrag={e} />
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Blättern */}
          {(daten?.seiten ?? 1) > 1 && (
            <div className="flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setSeite((s) => Math.max(1, s - 1))}
                disabled={seite <= 1}
                className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-[13px] font-semibold text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-40 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
                Zurück
              </button>
              <span className="text-[12.5px] text-gray-500 dark:text-gray-400">
                Seite {daten!.seite} von {daten!.seiten}
              </span>
              <button
                type="button"
                onClick={() => setSeite((s) => Math.min(daten!.seiten, s + 1))}
                disabled={seite >= (daten?.seiten ?? 1)}
                className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-2 text-[13px] font-semibold text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-40 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Weiter
                <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          )}

          <p className="text-[11.5px] leading-relaxed text-gray-400 dark:text-gray-500">
            Protokolliert werden Empfänger, Betreff, Zeitpunkt und Ergebnis — der Inhalt der Mails
            wird bewusst nicht gespeichert. Blindkopien stehen als eigene Zeile mit der Kennzeichnung
            „bcc". Ein fehlgeschlagener Versand wird bis zu viermal wiederholt; hier steht das
            Endergebnis.
          </p>
        </div>
      </div>

      {/* Filter als Slide-up auf dem Handy */}
      {filterOffen && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 lg:hidden"
          onClick={() => setFilterOffen(false)}
        >
          <div
            className="max-h-[85vh] w-full animate-slide-up overflow-y-auto rounded-t-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-[#1c2432]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-[15px] font-bold text-gray-900 dark:text-white">Filter</h2>
              <button
                type="button"
                onClick={() => setFilterOffen(false)}
                aria-label="Schließen"
                className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
              >
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
            {Felder}
            <button
              type="button"
              onClick={() => setFilterOffen(false)}
              className="mt-4 w-full rounded-lg bg-primary-600 px-3 py-2.5 text-[14px] font-bold text-white transition-colors hover:bg-primary-700"
            >
              {(g?.mails ?? 0).toLocaleString('de-DE')} Einträge anzeigen
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEmailProtokoll;
